import { useToast } from "@/components/ui/atoms/MToastProvider";
import { petCareService } from "@/services/petCareService";
import {
  PetCare,
  PetCareHistory,
  PetCareRequest,
  PetCareType,
} from "@/types/petCare";
import { useState } from "react";

interface PetCareState {
  deworming: PetCare | null;
  fleaTreatment: PetCare | null;
  history: PetCareHistory[];
  loading: boolean;
}

interface PetCareActions {
  fetchPetCare: () => Promise<void>;
  getPetCare: (petCareId: string) => Promise<PetCare>;
  createPetCare: (petCareRequest: PetCareRequest) => Promise<PetCare>;
  updatePetCare: (petCareRequest: PetCareRequest) => Promise<PetCare>;
  fetchHistory: (type: PetCareType) => Promise<void>;
}

const usePetCare = (memberId: string): PetCareState & PetCareActions => {
  const [deworming, setDeworming] = useState<PetCare | null>(null);
  const [fleaTreatment, setFleaTreatment] = useState<PetCare | null>(null);
  const [history, setHistory] = useState<PetCareHistory[]>([]);
  const [loading, setLoading] = useState(false);
  const { showToast } = useToast();

  const fetchPetCare = async () => {
    try {
      setLoading(true);
      const response = await petCareService.getPetCare(
        memberId,
        PetCareType.DEWORMING
      );
      setDeworming(response);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }

    try {
      setLoading(true);
      const fleaTreatmentResponse = await petCareService.getPetCare(
        memberId,
        PetCareType.FLEA_CONTROL
      );
      setFleaTreatment(fleaTreatmentResponse);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getPetCare = async (petCareId: string) => {
    try {
      setLoading(true);
      const response = await petCareService.getPetCareById(petCareId);
      return response;
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const createPetCare = async (
    petCareRequest: PetCareRequest
  ): Promise<PetCare> => {
    setLoading(true);
    try {
      const response = await petCareService.createPetCare(
        memberId,
        petCareRequest
      );
      setDeworming(response);
      return response;
    } catch (error) {
      console.error(error);
      showToast({
        message: "Please try again.",
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const updatePetCare = async (
    petCareRequest: PetCareRequest
  ): Promise<PetCare> => {
    setLoading(true);
    try {
      const response = await petCareService.updatePetCare(
        memberId,
        petCareRequest
      );
      setDeworming(response);
      return response;
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async (type: PetCareType) => {
    try {
      setLoading(true);
      const response = await petCareService.getHistory(memberId, type);
      setHistory(response);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return {
    deworming,
    fleaTreatment,
    history,
    loading,
    fetchPetCare,
    getPetCare,
    createPetCare,
    updatePetCare,
    fetchHistory,
  };
};

export default usePetCare;
